<template>
  <div class="note-discussion mt-8">
    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
      <p class="mt-4 text-grey text-caption">Loading conversation...</p>
    </div>

    <div v-else-if="thread">
      <h3 class="discussion-header text-h6 mb-6 font-weight-black d-flex align-center">
        <v-icon icon="mdi-comment-multiple-outline" class="mr-2" color="secondary"></v-icon>
        Community Feedback
      </h3>

      <!-- Community Replies -->
      <div class="replies-section">
        <template v-if="communityReplies.length > 0 && threadRoot">
          <ThreadReplyItem
            v-for="reply in communityReplies"
            :key="reply.post.uri"
            :reply="reply"
            :grouped="false"
            :is-author-chain="false"
            :author-handle="reply.post.author.handle"
            :root="threadRoot!"
            :replying-to-uri="replyingToUri"
            @reply="replyingToUri = $event.uri"
            @view-thread="viewThread"
            @delete="handleDelete($event)"
            @view-profile="viewProfile"
            @quote="handleQuote"
            @tag-click="handleTagClick"
            @reply-success="onReplySuccess"
            @reply-cancel="replyingToUri = ''"
          />
        </template>
        <v-alert v-else type="info" variant="tonal" class="rounded-xl border-white-05">
          No replies yet. Be the first to join the conversation!
        </v-alert>

        <div v-if="replyingToUri" class="reply-input-wrapper mt-4">
          <ReplyInput 
            :root="threadRoot!"
            :parent="{ uri: replyingToUri, cid: '' }"
            @success="onReplySuccess"
            @cancel="replyingToUri = ''"
          />
        </div>

        <!-- Persistent Reply Input at bottom (only if not replying to a specific sub-comment) -->
        <div v-else class="mt-8 border-t border-white-05 pt-6">
          <div v-if="!user" class="guest-cta-card pa-8 text-center rounded-xl bg-primary-05 border-primary-light">
            <v-icon icon="mdi-account-plus-outline" size="48" color="primary" class="mb-4"></v-icon>
            <h3 class="text-h6 font-weight-black mb-2">Join the Conversation</h3>
            <p class="text-body-2 text-grey-lighten-1 mb-6 mx-auto" style="max-width: 400px;">
              Sign in with your BlueSky account to leave a comment, react to posts, and connect with other gamedevs.
            </p>
            <v-btn
              color="primary"
              variant="flat"
              rounded="pill"
              class="text-none font-weight-black px-10"
              size="large"
              @click="$emit('login')"
            >
              Sign In to BlueSky
            </v-btn>
          </div>
          <div v-else>
            <div class="text-subtitle-2 font-weight-black text-grey mb-4 ml-1">Write a comment...</div>
            <ReplyInput 
              :root="threadRoot!"
              :parent="threadRoot!"
              @success="onReplySuccess"
            />
          </div>
        </div>

        <!-- Collapsible Source Info -->
        <div class="mt-12 pt-6 border-t border-white-05 opacity-60">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-caption font-weight-bold text-grey">CONVERSATION SOURCE</span>
            <v-btn
              variant="text"
              size="x-small"
              color="grey"
              class="text-none"
              :prepend-icon="showSource ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              @click="showSource = !showSource"
            >
              {{ showSource ? 'Hide Details' : 'View Source Thread' }}
            </v-btn>
          </div>
          
          <v-expand-transition>
            <div v-if="showSource" class="mt-4 pb-4">
              <PostItem 
                v-if="thread.post"
                :post="thread.post" 
                hide-note-embed
                @reply="replyingToUri = thread.post.uri"
                @view-thread="viewThread(thread.post)"
                @delete="handleDelete($event)"
                @view-profile="viewProfile"
                @quote="handleQuote"
                @tag-click="handleTagClick"
              />

              <InteractionStats 
                v-if="thread.post"
                class="mt-2"
                :repost-count="thread.post.repostCount || 0"
                :quote-count="thread.post.quoteCount || 0"
                :like-count="thread.post.likeCount || 0"
                @show-reposts="viewThread(thread.post)"
                @show-quotes="viewThread(thread.post)"
                @show-likes="viewThread(thread.post)"
              />
              <p class="text-center text-caption text-grey mt-2">This is the original post on BlueSky that hosts this discussion.</p>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-10 border rounded-xl border-dashed border-white-05">
      <template v-if="!user">
        <v-icon icon="mdi-lock-outline" size="48" color="grey-darken-2" class="mb-4"></v-icon>
        <h3 class="text-h6 font-weight-black mb-2">Community Protected</h3>
        <p class="text-grey mb-6">Login to view the discussion and participate.</p>
        <v-btn color="primary" variant="tonal" rounded="pill" class="text-none font-weight-bold px-8" @click="$emit('login')">
          Sign In
        </v-btn>
      </template>
      <template v-else>
        <v-icon icon="mdi-message-off-outline" size="48" color="grey-darken-2" class="mb-4"></v-icon>
        <p class="text-grey">The discussion thread could not be found or has been deleted.</p>
        <v-btn variant="text" color="primary" class="mt-2 text-none" @click="$emit('back')">Reset Discussion Gate</v-btn>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { atproto } from '@/services/atproto';
import { getCurrentUser } from '@/services/bluesky';
import ThreadReplyItem from './ThreadReplyItem.vue';
import ReplyInput from './ReplyInput.vue';
import PostItem from './PostItem.vue';
import InteractionStats from './InteractionStats.vue';

const props = defineProps<{
  postUri: string;
}>();

const emit = defineEmits(['back', 'login']);

const thread = ref<any>(null);
const loading = ref(true);
const replyingToUri = ref('');
const showSource = ref(false);
const user = ref(getCurrentUser());

const loadThread = async () => {
  if (!props.postUri) return;
  loading.value = true;
  try {
    thread.value = await atproto.getPostThread(props.postUri);
  } catch (e) {
    console.error('Failed to load thread:', e);
    thread.value = null;
  } finally {
    loading.value = false;
  }
};

const onReplySuccess = () => {
  replyingToUri.value = '';
  loadThread();
};

const threadRoot = computed<any>(() => {
  if (!thread.value?.post) return null;
  const record = thread.value.post.record || thread.value.post;
  if (record.reply?.root) {
    return {
      uri: record.reply.root.uri,
      cid: record.reply.root.cid
    };
  }
  return {
    uri: thread.value.post.uri,
    cid: thread.value.post.cid
  };
});

const communityReplies = computed(() => {
  if (!thread.value || !thread.value.replies) return [];
  
  // For notes, we just show all top-level replies as the start of community discussion
  return thread.value.replies.sort((a: any, b: any) => 
    new Date(a.post.indexedAt).getTime() - new Date(b.post.indexedAt).getTime()
  );
});

// Stubs for emitted events to prevent errors, though we might not fully implement them here
const viewThread = (post: any) => window.open(`https://bsky.app/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`, '_blank');
const viewProfile = (actor: string) => window.open(`https://bsky.app/profile/${actor}`, '_blank');
const handleDelete = (post: any) => {
  if (post.uri === props.postUri) {
    emit('back');
  } else {
    loadThread();
  }
};
const handleQuote = (post: any) => viewThread(post);
const handleTagClick = (tag: string) => window.open(`${window.location.origin}/t/${tag}`, '_blank');

onMounted(loadThread);
watch(() => props.postUri, loadThread);
</script>

<style scoped>
.note-discussion {
  max-width: 680px;
  margin: 0 auto;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.reply-input-wrapper {
  animation: expand 0.3s ease-out;
}

@keyframes expand {
  from { opacity: 0; max-height: 0; overflow: hidden; }
  to { opacity: 1; max-height: 500px; }
}

.guest-cta-card {
  background: rgba(var(--v-theme-primary), 0.05) !important;
  border: 1px solid rgba(var(--v-theme-primary), 0.1) !important;
}

.border-primary-light {
  border-color: rgba(var(--v-theme-primary), 0.2) !important;
}

.discussion-header {
  margin-left: 86px;
}

@media (max-width: 600px) {
  .discussion-header {
    margin-left: 0;
  }
}
</style>
